import { DonationUnitType, DonorStatus } from 'gql/types';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DateRangeType } from '../../../types/phlebotomist-dashboard';
import {
  DEFAULT_DATE_RANGE_FALLBACK,
  DONOR_TABLE_CONFIG_IDS,
  REGISTRATION_CONFIGS,
  UNIT_CATEGORY_IDS,
  UNIT_TABLE_CONFIG_IDS,
  VALID_DATE_RANGES,
} from '../constants';
import { IFiltersParams, IRecruiterParams, ITableParams } from '../types';

function parseLapsedDays(dataTableId: string | null): number | undefined {
  if (!dataTableId) return undefined;
  const match = dataTableId.match(/^lapsed-(\d+)-days$/);
  return match ? parseInt(match[1], 10) : undefined;
}

function parseRetiredDays(
  dataTableId: string | null
): { from: number; to: number | undefined } | undefined {
  if (!dataTableId) return undefined;
  const match = dataTableId.match(
    /^retired-unresponsive-(\d+)-(plus|\d+)-days$/
  );
  if (!match) return undefined;
  return {
    from: parseInt(match[1], 10),
    to: match[2] === 'plus' ? undefined : parseInt(match[2], 10),
  };
}

export function useUrlParams(): {
  table: ITableParams;
  filters: IFiltersParams;
  recruiter: IRecruiterParams;
} {
  const [searchParams] = useSearchParams();

  const dataTableId = searchParams.get('dataTable');
  const colVisKey = dataTableId || 'default';
  const unitCategoryId =
    dataTableId === 'ffp-donations'
      ? UNIT_CATEGORY_IDS.FFP
      : UNIT_CATEGORY_IDS.PRBC;
  const lapsedDays = parseLapsedDays(dataTableId);
  const isLapsed = !!lapsedDays;
  const retiredDays = parseRetiredDays(dataTableId);
  const isRetired = !!retiredDays;
  const isUnitTable = dataTableId
    ? UNIT_TABLE_CONFIG_IDS.has(dataTableId)
    : false;
  const isRegistration = dataTableId
    ? REGISTRATION_CONFIGS.has(dataTableId)
    : false;
  const isDonorTableConfig = dataTableId
    ? DONOR_TABLE_CONFIG_IDS.has(dataTableId) || isLapsed || isRetired
    : false;
  const isPending = dataTableId === 'pending-donors';
  const isRecruiterDonors = dataTableId === 'recruiter-donors';

  const donationUnitType =
    dataTableId === 'single-donors'
      ? DonationUnitType.Sgl
      : dataTableId === 'double-donors'
      ? DonationUnitType.Dbl
      : undefined;

  const recruiterName = searchParams.get('recruiterName');
  const recruiterId = searchParams.get('recruiterId');
  const isRecruiterLocations = dataTableId === 'recruiter-locations';

  const dateRange = useMemo(() => {
    if (!isUnitTable) return undefined;
    const dr = searchParams.get('dateRange');
    if (dr && VALID_DATE_RANGES.has(dr)) return dr as DateRangeType;
    if (dataTableId?.endsWith('-quarterly'))
      return 'quarterCal' as DateRangeType;
    return DEFAULT_DATE_RANGE_FALLBACK;
  }, [searchParams, dataTableId, isUnitTable]);

  const customDateRange = useMemo(() => {
    const cs = searchParams.get('customStart');
    const ce = searchParams.get('customEnd');
    if (cs && ce && dateRange === 'custom') {
      const start = new Date(cs);
      const end = new Date(ce);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        return { start, end };
      }
    }
    return undefined;
  }, [searchParams, dateRange]);

  const regionIds = useMemo(() => {
    const rIds = searchParams.get('regionIds');
    return rIds ? rIds.split(',') : undefined;
  }, [searchParams]);

  const officeIds = useMemo(() => {
    const oIds = searchParams.get('officeIds');
    return oIds ? oIds.split(',') : undefined;
  }, [searchParams]);

  const locationIds = useMemo(() => {
    const loc = searchParams.get('locationIds');
    return loc ? loc.split(',') : undefined;
  }, [searchParams]);

  const registrationRange = useMemo(() => {
    const rs = searchParams.get('registrationStart');
    const re = searchParams.get('registrationEnd');
    if (rs && re) return { start: rs, end: re };
    return undefined;
  }, [searchParams]);

  const activeDonorStatuses = useMemo(() => {
    const raw = searchParams.get('activeStatuses');
    if (!raw) return undefined;
    return raw.split(',') as DonorStatus[];
  }, [searchParams]);

  return {
    table: {
      dataTableId,
      colVisKey,
      unitCategoryId,
      isUnitTable,
      isRegistration,
      isDonorTableConfig,
      isLapsed,
      lapsedDays,
      isRetired,
      retiredDays,
      isPending,
      isRecruiterDonors,
      isRecruiterLocations,
      donationUnitType,
    },
    filters: {
      dateRange,
      customDateRange,
      regionIds,
      officeIds,
      locationIds,
      registrationRange,
      activeDonorStatuses,
    },
    recruiter: {
      recruiterName,
      recruiterId,
    },
  };
}
